import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiClock, FiX, FiPlus, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const JURISDICTIONS = [['TR', 'Türkiye'], ['ZW', 'Zimbabwe']];
const DEADLINE_TYPES = [
  ['VAT', 'VAT/KDV Return'], ['EDEFTER', 'e-Defter Submission'], ['PAYE', 'PAYE'],
  ['QPD', 'Quarterly Payment Date (ZIMRA)'], ['OTHER', 'Other'],
];

const emptyForm = () => ({ jurisdiction: 'TR', deadline_type: 'VAT', description: '', due_date: '', fiscal_year: new Date().getFullYear() });

const TaxDeadlines = () => {
  const { isFinanceLeadership } = useAuth();
  const [deadlines, setDeadlines] = useState([]);
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = showUpcomingOnly ? await financeAPI.taxDeadlines.upcoming(90) : await financeAPI.taxDeadlines.list();
      setDeadlines(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load tax deadlines.');
    } finally {
      setLoading(false);
    }
  }, [showUpcomingOnly]);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.taxDeadlines.create(form);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create deadline.');
    } finally {
      setSaving(false);
    }
  };

  const complete = async (deadline) => {
    const notes = window.prompt('Notes (optional)?') || '';
    setBusyId(deadline.id);
    setError(null);
    try { await financeAPI.taxDeadlines.complete(deadline.id, notes); load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to mark complete.'); }
    finally { setBusyId(null); }
  };

  const daysUntil = (dateStr) => Math.round((new Date(dateStr) - new Date()) / 86400000);

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiClock size={22} />Tax Deadline Calendar</h1>
          <p style={fs.pageSubtitle}>Both jurisdictions — Turkish VAT/e-Defter, ZIMRA VAT/PAYE/QPD.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowUpcomingOnly((v) => !v)} style={fs.neutralButton}>{showUpcomingOnly ? 'Showing: Next 90 Days' : 'Showing: All'}</button>
          {isFinanceLeadership() && <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Deadline</button>}
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div><div style={fs.label}>Jurisdiction</div>
            <select value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              {JURISDICTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Type</div>
            <select value={form.deadline_type} onChange={(e) => setForm({ ...form, deadline_type: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              {DEADLINE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Description</div><input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Due Date</div><input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Fiscal Year</div><input required type="number" value={form.fiscal_year} onChange={(e) => setForm({ ...form, fiscal_year: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create'}</button>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : deadlines.length === 0 ? (
        <div style={fs.emptyState}><FiClock size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No deadlines in range.</div></div>
      ) : (
        <div style={fs.tableWrap}>
          <table style={fs.table}>
            <thead><tr style={fs.tableHeadRow}>{['Jurisdiction', 'Type', 'Description', 'Due', 'Status', ''].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
            <tbody>
              {deadlines.map((d, i) => {
                const busy = busyId === d.id;
                const days = daysUntil(d.due_date);
                return (
                  <tr key={d.id} style={{ borderBottom: i < deadlines.length - 1 ? '1px solid #111' : 'none' }}>
                    <td style={fs.td}>{JURISDICTIONS.find((j) => j[0] === d.jurisdiction)?.[1] || d.jurisdiction}</td>
                    <td style={{ ...fs.td, color: fs.colors.text }}>{DEADLINE_TYPES.find((t) => t[0] === d.deadline_type)?.[1] || d.deadline_type}</td>
                    <td style={{ ...fs.td, color: fs.colors.textBright }}>{d.description}</td>
                    <td style={fs.td}>{d.due_date} {!d.completed && <span style={{ color: days < 7 ? fs.colors.red : fs.colors.textFaint }}>({days}d)</span>}</td>
                    <td style={fs.td}>{d.completed ? <span style={fs.badge(fs.colors.green)}>DONE</span> : <span style={fs.badge(fs.colors.amber)}>PENDING</span>}</td>
                    <td style={fs.td}>
                      {!d.completed && isFinanceLeadership() && (
                        <button onClick={() => complete(d)} disabled={busy} style={fs.successButton}><FiCheck size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Mark Done</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaxDeadlines;
